/* GetNails: search discovery + visible search drawer text without diacritics. */
(() => {
  // Only presentation text is changed. Product URLs, search queries entered by visitors,
  // form values, and Shopify's source product/collection data remain untouched.
  const withoutDiacritics = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const excludedElements = 'script, style, noscript, template, textarea, svg, [contenteditable="true"]';
  const visibleAttributes = ['placeholder', 'aria-label', 'title', 'alt'];

  const normalizeAttributes = (element) => {
    for (const attr of visibleAttributes) {
      if (!element.hasAttribute(attr)) continue;
      const current = element.getAttribute(attr);
      const updated = withoutDiacritics(current);
      if (updated !== current) element.setAttribute(attr, updated);
    }
  };

  const normalizeTextNode = (node) => {
    if (!node.parentElement || node.parentElement.closest(excludedElements)) return;
    const updated = withoutDiacritics(node.nodeValue);
    if (updated !== node.nodeValue) node.nodeValue = updated;
  };

  const normalizeVisibleText = (node) => {
    if (node.nodeType === 3) {
      normalizeTextNode(node);
      return;
    }
    if (node.nodeType !== 1 || node.matches(excludedElements)) return;

    normalizeAttributes(node);
    node.querySelectorAll('[placeholder], [aria-label], [title], [alt]').forEach(normalizeAttributes);

    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) normalizeTextNode(walker.currentNode);
  };

  const init = (drawer) => {
    if (!drawer || drawer.dataset.gnSearchDiscoveryReady === 'true') return;
    drawer.dataset.gnSearchDiscoveryReady = 'true';

    // Initial drawer copy (including existing Theme Editor settings).
    normalizeVisibleText(drawer);
    if ('MutationObserver' in window) {
      // Predictive results arrive asynchronously. Convert newly inserted copy,
      // keeping the input field's actual value and Shopify links intact.
      new MutationObserver((changes) => {
        for (const change of changes) {
          if (change.type === 'characterData') {
            normalizeTextNode(change.target);
          } else if (change.type === 'attributes') {
            normalizeAttributes(change.target);
          } else {
            change.addedNodes.forEach(normalizeVisibleText);
          }
        }
      }).observe(drawer, {
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: visibleAttributes,
        subtree: true,
      });
    }

    const form = drawer.querySelector('.search__form');
    const searchInput = drawer.querySelector('.search__form input[type="search"]');
    if (form && searchInput && 'MutationObserver' in window) {
      const syncExpanded = () => {
        searchInput.setAttribute('aria-expanded', String(form.hasAttribute('results') && !!searchInput.value.trim()));
      };
      new MutationObserver(syncExpanded).observe(form, {
        attributes: true,
        attributeFilter: ['results'],
      });
      searchInput.addEventListener('input', syncExpanded);
    }


    // Use the theme's AJAX cart flow so the badge and lateral cart refresh
    // immediately, including results swapped in after each search request.
    drawer.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-gn-search-add-to-cart]');
      if (!button || !drawer.contains(button) || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const entry = button.closest('.gn-search-product-entry');
      const select = entry?.querySelector('[data-gn-search-variant-select]');
      const option = select?.selectedOptions?.[0];
      const variantId = Number(select ? select.value : button.dataset.gnVariantId);
      const quantity = Math.max(1, Number(option?.dataset.minQuantity || button.dataset.gnMinQuantity || 1));
      const feedback = entry?.querySelector('.gn-search-cart-feedback');
      if (!Number.isSafeInteger(variantId) || variantId <= 0) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = drawer.dataset.gnSearchAdding || 'Adding...';
      if (feedback) {
        feedback.hidden = true;
        feedback.textContent = '';
      }

      const theme = window.FoxTheme;
      const routes = theme?.routes || {};
      const cartAddUrl = routes.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
      const cartUrl = routes.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      const cartJsonUrl = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
      const cartAjaxAddUrl = cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;
      const cartDrawer = document.querySelector('cart-drawer');
      const useCartDrawer = Boolean(cartDrawer && theme?.settings?.cartType === 'drawer' && !document.body.classList.contains('cart-template'));
      const sections = [];
      if (useCartDrawer) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections },
        }));
      }

      try {
        const payload = {
          items: [{ id: variantId, quantity }],
          ...(useCartDrawer ? { sections, sections_url: window.location.pathname } : {}),
        };
        const addResponse = await fetch(cartAjaxAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const added = await addResponse.json();
        if (!addResponse.ok || added.status) {
          throw new Error(added.description || added.message || drawer.dataset.gnSearchAddError || 'The product could not be added.');
        }

        const cartResponse = await fetch(cartJsonUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!cartResponse.ok) throw new Error(drawer.dataset.gnSearchCartError || 'The cart could not be updated.');
        const cart = await cartResponse.json();
        if (added.sections) cart.sections = added.sections;

        theme?.pubsub?.publish?.(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        document.dispatchEvent(new CustomEvent('product-ajax:added', {
          detail: { product: added },
        }));

        if (useCartDrawer) {
          if (drawer.open && typeof drawer.hide === 'function') {
            const closed = drawer.hide();
            // A browser that skips overlay transitions should not prevent opening the cart.
            if (closed?.then) {
              await Promise.race([closed, new Promise((resolve) => setTimeout(resolve, 450))]);
            }
          }
          cartDrawer.show();
        } else {
          window.location.assign(cartUrl);
        }
      } catch (error) {
        if (feedback) {
          feedback.textContent = error.message || drawer.dataset.gnSearchGenericError || 'Something went wrong. Please try again.';
          feedback.hidden = false;
        }
      } finally {
        button.textContent = originalLabel;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });

    drawer.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-gn-search-term]');
      if (!chip || !drawer.contains(chip)) return;

      const input = drawer.querySelector('.search__form input[type="search"]');
      if (!input) return;

      const term = chip.getAttribute('data-gn-search-term');
      if (!term) return;

      // The selected popular search is displayed without diacritics as well.
      input.value = withoutDiacritics(term);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus({ preventScroll: true });

      const scroller = drawer.querySelector('.drawer__content > .v-scrollable');
      if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const boot = () => document.querySelectorAll('#SearchDrawer').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Shopify theme editor can replace the section while the page stays open.
  document.addEventListener('shopify:section:load', boot);
})();
